/**
 * Network quality detection hook.
 *
 * Combines two signals:
 * 1. Network Information API (Android Chrome only) — effectiveType, downlink, rtt
 * 2. Ping probe latency from useOnlineStatus (all browsers incl. iOS Safari)
 *
 * Returns: 'good' | 'degraded' | 'poor' | 'offline'
 * Debounced: requires 2 consecutive readings before transitioning.
 */

import { useEffect, useRef, useState } from 'react'
import { useOnlineStatus, useProbeLatency } from './useOnlineStatus'

export type NetworkQuality = 'good' | 'degraded' | 'poor' | 'offline'

interface NetworkInformation extends EventTarget {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g'
  downlink?: number
  rtt?: number
}

function getNetworkConnection(): NetworkInformation | undefined {
  const nav = navigator as Navigator & { connection?: NetworkInformation }
  return nav.connection
}

function qualityFromProbeLatency(latencyMs: number | null): NetworkQuality {
  if (latencyMs === null) return 'poor'
  if (latencyMs < 300) return 'good'
  if (latencyMs <= 1500) return 'degraded'
  return 'poor'
}

function qualityFromNetworkInfo(conn: NetworkInformation): NetworkQuality {
  const effectiveType = conn.effectiveType
  if (!effectiveType) return 'good' // Unknown — optimistic

  switch (effectiveType) {
    case '4g':
      return 'good'
    case '3g':
      return 'degraded'
    case '2g':
    case 'slow-2g':
      return 'poor'
    default:
      return 'good'
  }
}

function worstQuality(a: NetworkQuality, b: NetworkQuality): NetworkQuality {
  const rank: Record<NetworkQuality, number> = {
    offline: 0,
    poor: 1,
    degraded: 2,
    good: 3,
  }
  return rank[a] <= rank[b] ? a : b
}

const DEBOUNCE_COUNT = 2

export function useNetworkQuality(): NetworkQuality {
  const online = useOnlineStatus()
  const probeLatency = useProbeLatency()
  const [quality, setQuality] = useState<NetworkQuality>('good')
  const consecutiveRef = useRef<{ quality: NetworkQuality; count: number }>({
    quality: 'good',
    count: 0,
  })

  // Listen to Network Information API changes (Android Chrome)
  const [netInfoQuality, setNetInfoQuality] = useState<NetworkQuality>('good')

  useEffect(() => {
    const conn = getNetworkConnection()
    if (!conn) return

    const update = () => {
      setNetInfoQuality(qualityFromNetworkInfo(conn))
    }
    update()

    conn.addEventListener('change', update)
    return () => conn.removeEventListener('change', update)
  }, [])

  // Combine signals with debouncing
  useEffect(() => {
    if (!online) {
      setQuality('offline')
      consecutiveRef.current = { quality: 'offline', count: DEBOUNCE_COUNT }
      return
    }

    const probeQuality = qualityFromProbeLatency(probeLatency)
    const combined = worstQuality(probeQuality, netInfoQuality)

    const prev = consecutiveRef.current
    if (combined === prev.quality) {
      if (prev.count < DEBOUNCE_COUNT) {
        consecutiveRef.current = { quality: combined, count: prev.count + 1 }
      }
      if (prev.count + 1 >= DEBOUNCE_COUNT) {
        setQuality(combined)
      }
    } else {
      consecutiveRef.current = { quality: combined, count: 1 }
      // Immediate transition to worse quality (for safety)
      const rank: Record<NetworkQuality, number> = {
        offline: 0,
        poor: 1,
        degraded: 2,
        good: 3,
      }
      if (rank[combined] < rank[quality]) {
        setQuality(combined)
      }
    }
  }, [online, probeLatency, netInfoQuality, quality])

  return quality
}
